import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import {
  ArrowUpRight,
  FileText,
  ClipboardList,
  Wallet,
  TrendingUp,
  CalendarClock,
  UserPlus,
} from "lucide-react";
import useResource from "../../shared/useResource";
import { useSession } from "../../auth/SessionProvider";
import { Header, Status, Field, money, date } from "../../shared/UI";
import DashboardCharts from "./DashboardCharts";
export default function InicioPage() {
  const { session } = useSession(),
    nav = useNavigate();
  const today = new Date().toLocaleDateString("en-CA");
  const [from, setFrom] = useState(today.slice(0, 8) + "01"),
    [until, setUntil] = useState(today);
  const { data, error, loading, reload } = useResource(
    `comercial-app/inicio?desde=${from}&hasta=${until}`,
  );
  const indicators = {
    cotizaciones: {
      label: "Cotizaciones",
      Icon: FileText,
      to: "/ventas/cotizaciones",
    },
    pedidos: { label: "Pedidos", Icon: ClipboardList, to: "/ventas/pedidos" },
    ventas: { label: "Ventas netas", Icon: TrendingUp, to: "/reportes/ventas" },
  };
  return (
    <>
      <Header
        title={`Hola, ${session.usuario.nombre?.split(" ")[0] || "bienvenido"}`}
        description="Esto es lo que sucede en tu operación comercial."
      />
      <div className="dashboard-period">
        <div>
          <span className="eyebrow">RESUMEN DEL NEGOCIO</span>
          <p>
            {session.scope.all_agents
              ? "Vista de los registros autorizados"
              : "Vista de tus registros asignados"}
          </p>
        </div>
        <div className="date-pair">
          <Field label="Desde" type="date" value={from} onChange={setFrom} />
          <Field label="Hasta" type="date" value={until} onChange={setUntil} />
        </div>
      </div>
      <Status loading={loading} error={error} onRetry={reload} />
      {!error && data && (
        <>
          <div className="indicator-grid">
            {Object.entries(data.indicadores).map(([key, values]) => {
              const c = indicators[key],
                Icon = c.Icon;
              return (
                <button
                  className="indicator"
                  key={key}
                  onClick={() => nav(`${c.to}?desde=${from}&hasta=${until}`)}
                >
                  <div className="indicator-top">
                    <span>{c.label}</span>
                    <Icon size={21} />
                  </div>
                  {values.length ? (
                    values.map((v) => (
                      <div key={v.moneda}>
                        <strong>{money(v.importe, v.moneda)}</strong>
                        <small>
                          {v.moneda} · {v.cantidad} documentos
                        </small>
                      </div>
                    ))
                  ) : (
                    <>
                      <strong>{money(0)}</strong>
                      <small>Sin movimientos en el período</small>
                    </>
                  )}
                  <span className="indicator-link">
                    Ver documentos <ArrowUpRight size={16} />
                  </span>
                </button>
              );
            })}
            {session.modules.cartera && (
              <button className="indicator" onClick={() => nav("/cobranza")}>
                <div className="indicator-top">
                  <span>Por cobrar</span>
                  <Wallet size={21} />
                </div>
                {data.cartera_actual.length ? (
                  data.cartera_actual.map((v) => (
                    <div key={v.moneda}>
                      <strong>{money(v.saldo, v.moneda)}</strong>
                      <small>
                        {v.moneda} · Vencido: {money(v.vencido, v.moneda)}
                      </small>
                    </div>
                  ))
                ) : (
                  <>
                    <strong>{money(0)}</strong>
                    <small>Sin saldos pendientes</small>
                  </>
                )}
                <span className="indicator-link">
                  Saldo actual <ArrowUpRight size={16} />
                </span>
              </button>
            )}
          </div>
          <section className="quick-actions-section">
            <div className="section-heading">
              <h2>Accesos rápidos</h2>
              <span>Tu día a día, en un clic</span>
            </div>
            <div className="quick-actions">
              {[
                [
                  "Nueva cotización",
                  "cotizaciones",
                  "/ventas/cotizaciones",
                  FileText,
                ],
                ["Nuevo pedido", "pedidos", "/ventas/pedidos", ClipboardList],
                ["Nuevo prospecto", "prospectos", "/crm/prospectos", UserPlus],
                [
                  "Registrar seguimiento",
                  "seguimientos",
                  "/crm/seguimientos",
                  CalendarClock,
                ],
              ]
                .filter(([, module]) => session.modules[module])
                .map(([label, , path, Icon]) => (
                  <Button
                    key={path}
                    variant="outline-primary"
                    onClick={() => nav(path + "?nuevo=1")}
                  >
                    <Icon size={21} aria-hidden="true" />
                    <span>{label}</span>
                    <ArrowUpRight size={16} aria-hidden="true" />
                  </Button>
                ))}
            </div>
          </section>
          <DashboardCharts
            data={data}
            onReceivables={
              session.modules.cartera ? () => nav("/cobranza") : null
            }
          />
          <div className="dashboard-bottom">
            <section className="surface">
              <div className="section-heading">
                <div>
                  <CalendarClock size={21} />
                  <h2>Próximos seguimientos</h2>
                </div>
                {session.modules.seguimientos && (
                  <Button
                    variant="link"
                    onClick={() => nav("/crm/seguimientos")}
                  >
                    Ver todos
                  </Button>
                )}
              </div>
              {data.seguimientos.length ? (
                data.seguimientos.map((r) => (
                  <button
                    className="activity"
                    key={r.id}
                    onClick={() => nav("/crm/seguimientos")}
                  >
                    <span className="activity-dot" />
                    <div>
                      <strong>{r.cliente?.nombre || "Seguimiento"}</strong>
                      <p>{r.notas_seguimiento || "Sin notas"}</p>
                    </div>
                    <small>{date(r.fecha_vencimiento)}</small>
                  </button>
                ))
              ) : (
                <div className="empty">
                  {session.modules.seguimientos
                    ? "No tienes seguimientos pendientes."
                    : "Consulta los módulos disponibles en el menú."}
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </>
  );
}
